import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  Flex,
  Image,
  Input,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import Insta from "../../images/insta_logo.png";
import Facebook from "../../images/facebook_logo.png";
import Youtube from "../../images/youtube_logo.png";
import avatar from "../../../src/images/Avatar.png";
import tiktok from "../../images/tictok_logo.png";
import message from "../../images/message.png";
import { useParams } from "react-router-dom";
import {
  adminContentConfirm,
  adminContentFeedback,
  adminContentLiveSchedule,
  adminContentReject,
  contentsCreatorDetail,
} from "../../api";

function InfoBox({ label, value }) {
  return (
    <Box
      flex="1"
      border="1px solid #E5E7EB"
      borderRadius="12px"
      p="10px"
      textAlign="center"
    >
      <Text fontSize="14px" fontWeight={500} color="#5D6979">
        {label}
      </Text>
      <Text fontSize="16px" fontWeight={600}>
        {value}
      </Text>
    </Box>
  );
}

const PLATFORM_ICONS = {
  instagram: Insta,
  facebook: Facebook,
  tiktok: tiktok,
  youtube: Youtube,
};

export default function ContentsDetailBottom(data) {
  const { id } = useParams();
  const toast = useToast();

  const [feedbackState, setFeedbackState] = useState([]);
  const [creatorData, setCreatorData] = useState([]);
  const [scheduleByContentId, setScheduleByContentId] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const latestAccordionIndex =
    creatorData.length > 0
      ? creatorData.reduce((latestIdx, item, idx) => {
          const latestAt = new Date(
            creatorData[latestIdx]?.resisterAt ?? 0
          ).getTime();
          const currentAt = new Date(item?.resisterAt ?? 0).getTime();
          return currentAt > latestAt ? idx : latestIdx;
        }, 0)
      : -1;

  const [selected, setSelected] = useState(data.data[0] ?? []);

  const loadCreatorContents = async () => {
    if (!selected?.creatorProfileId) return;

    try {
      const res = await contentsCreatorDetail(id, selected.creatorProfileId);

      const detail = res?.data?.creatorDetail ?? {};
      const contents = res?.data?.contents ?? [];

      setSelected((prev) =>
        JSON.stringify(prev) === JSON.stringify(detail) ? prev : detail
      );

      setCreatorData(contents);

      setFeedbackState(contents.map((c) => c.feedbackItems ?? []));
    } catch (err) {
      console.error("contentsCreatorDetail error:", err);
      setCreatorData([]);
    }
  };

  useEffect(() => {
    loadCreatorContents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.creatorProfileId]);

  const handleAction = async (contentId, actionKey, action, successTitle) => {
    try {
      setActionLoading(`${actionKey}-${contentId}`);
      await action();
      toast({
        title: successTitle,
        status: "success",
        duration: 1800,
        isClosable: true,
      });
      await loadCreatorContents();
    } catch (e) {
      toast({
        title: "처리 실패",
        description:
          e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          e?.message ||
          "콘텐츠 처리 중 오류가 발생했습니다.",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleFeedback = (item) => {
    const message = window.prompt("크리에이터에게 전달할 피드백을 입력하세요.");
    if (!message?.trim()) return;
    handleAction(
      item.contentId,
      "feedback",
      () =>
        adminContentFeedback({
          proposalId: id,
          creatorId: selected.creatorProfileId,
          contentId: item.contentId,
          message: message.trim(),
        }),
      "피드백을 등록했습니다."
    );
  };

  const handleSchedule = (item) => {
    const values = scheduleByContentId[item.contentId] ?? {};
    if (!values.startAt) {
      toast({
        title: "라이브 시작일을 입력해주세요.",
        status: "warning",
        duration: 1800,
        isClosable: true,
      });
      return;
    }
    handleAction(
      item.contentId,
      "schedule",
      () =>
        adminContentLiveSchedule({
          proposalId: id,
          creatorId: selected.creatorProfileId,
          contentId: item.contentId,
          startAt: values.startAt,
          endAt: values.endAt || null,
        }),
      "라이브 일정을 저장했습니다."
    );
  };

  return (
    <Flex
      w="100%"
      h={{ base: "none", md: "calc(100vh - 100px)" }}
      borderRadius="12px"
      flexDir={{ base: "column", md: "row" }}
    >
      {/* ========== LEFT: Creator List ========== */}
      <Box w={{ base: "100%", md: "30%" }} bg="white" p="4" overflowY="auto">
        <Text fontSize="24" fontWeight="700" mb="4">
          믹스된 크리에이터 목록 ({data.data?.length})
        </Text>

        <VStack align="stretch" spacing="3">
          {data.data?.map((c) => (
            <Flex
              key={c.creatorProfileId}
              p="3"
              bg={
                selected?.creatorProfileId === c.creatorProfileId
                  ? "#E9F2FF"
                  : "white"
              }
              border={
                selected?.creatorProfileId === c.creatorProfileId
                  ? "1px solid #0054C0"
                  : "1px solid #D1D4D9"
              }
              borderRadius="16px"
              cursor="pointer"
              flexDir="column"
              onClick={() => setSelected(c)}
              gap="12px"
            >
              <Flex gap="12px" align="center">
                <Image src={c.avatarUrl ?? avatar} w="48px" h="48px" />
                <Text fontWeight="600">{c.username}</Text>
                <Image
                  w="24px"
                  h="24px"
                  src={
                    PLATFORM_ICONS[c.platform?.toLowerCase()] ??
                    "/images/default.png"
                  }
                  alt="platform-icon"
                />
              </Flex>

              {/* 기본 정보 */}
              <Flex w="100%" gap="8px" justify="center" align="center">
                {["팔로워", "게시물", "조회수"].map((label, i) => {
                  const val = [c.followers, c.posts, c.avgViews][i];
                  return (
                    <Flex
                      key={label}
                      w="100%"
                      gap="4px"
                      flexDir="column"
                      align="center"
                      bg="white"
                      border="1px solid #D1D4D9"
                      borderRadius="8px"
                      p="8px"
                    >
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        {label}
                      </Text>
                      <Text fontSize="16px" fontWeight={600}>
                        {val?.toLocaleString() ?? 0}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
          ))}
        </VStack>
      </Box>

      {/* ========== RIGHT: Selected Creator Detail ========== */}
      <Box
        flex="1"
        bg="white"
        py="20px"
        px={{ base: "10px", md: "20px" }}
        borderRadius="16px"
        border="1px solid #E8EAEC"
        overflowY="auto"
      >
        <Text fontSize="20px" fontWeight="600" mb="4">
          선택된 크리에이터 상세 정보
        </Text>

        <Flex gap="20px" flexDir="column">
          <Flex gap="20px" flexDir={{ base: "column", md: "row" }}>
            <InfoBox label="이름" value={selected?.displayName} />
            <InfoBox label="이메일" value={selected?.email} />
          </Flex>

          <Flex gap="20px">
            <InfoBox label="팔로워" value={selected?.followers} />
            <InfoBox label="게시물" value={selected?.posts} />
            <InfoBox label="평균 조회수" value={selected?.avgViews} />
          </Flex>
          <Flex gap="20px">
            <InfoBox
              label="성별"
              value={
                selected?.gender === "male"
                  ? "남성"
                  : selected?.gender === "female"
                  ? "여성"
                  : "기타"
              }
            />
            <InfoBox label="생년월일" value={selected?.birthDate} />
            <InfoBox label="국가/언어" value={selected?.language} />
          </Flex>
          <Flex
            w="100%"
            gap="20px"
            border="1px solid #D1D4D9"
            borderRadius="8px"
            p="12px"
            justifySelf="start"
            align="center"
          >
            <Text fontSize="16px" fontWeight={400}>
              산업 카테고리
            </Text>
            <Flex gap="8px">
              {selected?.industryCategories?.map((i) => (
                <Flex
                  key={i.id}
                  bg="#E9F2FF"
                  px="8px"
                  h="24px"
                  align="center"
                  justify="center"
                  borderRadius="6px"
                  fontSize="12px"
                  fontWeight={500}
                >
                  {i.nameKo}
                </Flex>
              ))}
            </Flex>
          </Flex>
          <Flex
            w="100%"
            gap="12px"
            border="1px solid #D1D4D9"
            borderRadius="8px"
            p="12px"
            align="center"
          >
            {/* 왼쪽 타이틀 */}
            <Text
              fontSize="16px"
              fontWeight={400}
              flexShrink={0}
              color="gray.700"
            >
              라이프스타일 카테고리
            </Text>

            {/* 태그 스크롤 영역 */}
            <Box
              flex="1"
              overflowX="auto"
              overflowY="hidden"
              sx={{
                WebkitOverflowScrolling: "touch",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <Flex gap="8px" wrap="nowrap" width="fit-content">
                {selected?.lifestyleCategories?.map((i) => (
                  <Flex
                    key={i.id}
                    bg="#E9F2FF"
                    px="10px"
                    h="26px"
                    align="center"
                    justify="center"
                    borderRadius="6px"
                    fontSize="12px"
                    fontWeight={500}
                    flexShrink={0}
                    whiteSpace="nowrap"
                  >
                    {i.nameKo}
                  </Flex>
                ))}
              </Flex>
            </Box>
          </Flex>

          <Divider />

          <Box>
            <Text fontWeight="600" fontSize="18px" mb="30px">
              콘텐츠 검수 목록
            </Text>

            <Accordion
              key={`admin-accordion-${creatorData.length}-${latestAccordionIndex}`}
              allowMultiple
              defaultIndex={latestAccordionIndex >= 0 ? [latestAccordionIndex] : []}
            >
              {creatorData?.map((item, idx) => {
                const round = idx + 1;
                const fb = feedbackState[idx];
                return (
                  <AccordionItem
                    key={item.contentId}
                    borderBottom="1px solid #E8EAEC"
                    mb="2"
                    overflow="hidden"
                  >
                    <AccordionButton
                      _expanded={{ bg: "#F9FAFB" }}
                      justifyContent="space-between"
                      px={{ base: 0, md: "10px" }}
                    >
                      <Flex gap="12px" align="center">
                        <Flex
                          borderRadius="12px"
                          justify="center"
                          align="center"
                          p="8px"
                          bg="linear-gradient(358deg,rgba(242, 246, 252, 1) 30%, 
                          rgba(228, 236, 248, 1) 90%);"
                          fontSize="20px"
                          fontWeight={500}
                        >
                          {round}회차
                        </Flex>
                        <Text fontSize="20px" fontWeight={500} color="#5D6979">
                          {item.resisterAt?.slice(0, 10).replace(/-/g, ".")}
                        </Text>
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel p={0} pb={4} bg="white">
                      <Text fontSize="16px" fontWeight={400} my="12px">
                        제작한 콘텐츠
                      </Text>

                      <Flex
                        gap="20px"
                        flexDir={{ base: "column", md: "row" }}
                        h="100%"
                      >
                        <Flex
                          w={{ base: "100%", md: "40%" }}
                          flexDir="column"
                          gap="20px"
                          h="100%"
                        >
                          {item.contentType === "image" && (
                            <Image
                              src={item.contentUrl}
                              alt="content"
                              borderRadius="md"
                            />
                          )}
                          {item.contentType === "video" && (
                            <video
                              controls
                              src={item.contentUrl}
                              style={{
                                maxWidth: "100%",
                                height: "auto",
                              }}
                            />
                          )}

                          <Flex flexDir="column" gap="10px">
                            <Text>콘텐츠 설명</Text>
                            <Flex bg="#F4F7FA" borderRadius="8px" p="12px">
                              {item.description || "설명 없음"}
                            </Flex>
                          </Flex>
                        </Flex>

                        {/* ===== 피드백 관리 ===== */}
                        <Flex
                          w={{ base: "100%", md: "60%" }}
                          flexDir="column"
                          gap="10px"
                          flex="1" // 부모 안에서 이 영역이 늘어나도록
                          minH={0} // 스크롤 작동 핵심 !!!
                        >
                          {/* --- 피드백 리스트 (스크롤 영역) --- */}
                          <Flex
                            flexDir="column"
                            gap="10px"
                            overflowY="auto"
                            pr="2"
                            maxH={{
                              base: "300px",
                              lg: "270px",
                            }} // 원하는 높이 지정 (반드시 필요)
                            minH={0} // height 계산 오류 방지
                          >
                            {fb?.map((fbText, fbIdx) => (
                              <Flex
                                key={fbIdx}
                                flexDir="column"
                                gap="12px"
                                borderBottom="1px solid #CAC4D0"
                                pb="10px"
                              >
                                <Flex align="center" gap="10px">
                                  <Image src={message} />
                                  <Text>
                                    {
                                      item.feedbackItems[0]
                                        .advertiserDisplayName
                                    }
                                  </Text>
                                </Flex>
                                <Flex
                                  p="8px 12px"
                                  borderRadius="8px"
                                  border="1px solid #D1D4D9"
                                  justify="space-between"
                                  bg="#F9FAFB"
                                >
                                  <Text fontSize="14px">
                                    {fbText?.message ?? ""}
                                  </Text>
                                </Flex>
                              </Flex>
                            ))}
                          </Flex>
                          <Flex gap="8px" wrap="wrap">
                            {(() => {
                              const canProcess = item.contentStatus === "REVIEW";
                              return (
                                <>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              isDisabled={!canProcess}
                              isLoading={actionLoading === `confirm-${item.contentId}`}
                              onClick={() =>
                                handleAction(
                                  item.contentId,
                                  "confirm",
                                  () =>
                                    adminContentConfirm({
                                      proposalId: id,
                                      creatorId: selected.creatorProfileId,
                                      contentId: item.contentId,
                                    }),
                                  "콘텐츠를 승인했습니다."
                                )
                              }
                            >
                              승인
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="red"
                              isDisabled={!canProcess}
                              isLoading={actionLoading === `reject-${item.contentId}`}
                              onClick={() =>
                                handleAction(
                                  item.contentId,
                                  "reject",
                                  () =>
                                    adminContentReject({
                                      proposalId: id,
                                      creatorId: selected.creatorProfileId,
                                      contentId: item.contentId,
                                    }),
                                  "콘텐츠를 반려했습니다."
                                )
                              }
                            >
                              반려
                            </Button>
                                </>
                              );
                            })()}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleFeedback(item)}
                              isLoading={actionLoading === `feedback-${item.contentId}`}
                            >
                              피드백
                            </Button>
                          </Flex>
                          {item.contentStatus === "CONFIRMED" && (
                            <Flex gap="8px" wrap="wrap" align="center">
                              <Input
                                type="date"
                                size="sm"
                                maxW="160px"
                                value={scheduleByContentId[item.contentId]?.startAt ?? ""}
                                onChange={(e) =>
                                  setScheduleByContentId((prev) => ({
                                    ...prev,
                                    [item.contentId]: {
                                      ...(prev[item.contentId] ?? {}),
                                      startAt: e.target.value,
                                    },
                                  }))
                                }
                              />
                              <Input
                                type="date"
                                size="sm"
                                maxW="160px"
                                value={scheduleByContentId[item.contentId]?.endAt ?? ""}
                                onChange={(e) =>
                                  setScheduleByContentId((prev) => ({
                                    ...prev,
                                    [item.contentId]: {
                                      ...(prev[item.contentId] ?? {}),
                                      endAt: e.target.value,
                                    },
                                  }))
                                }
                              />
                              <Button
                                size="sm"
                                bg="#0054C0"
                                color="white"
                                isLoading={actionLoading === `schedule-${item.contentId}`}
                                onClick={() => handleSchedule(item)}
                              >
                                라이브 일정 저장
                              </Button>
                            </Flex>
                          )}
                        </Flex>
                      </Flex>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
}
